import React from "react"
import styled, { keyframes, css } from "styled-components"
import {
  CardStyle,
  HeadingOne,
  Link,
  Button,
  Arrow,
  Paragraph,
} from "buoy-core"
import { responsive } from "../../common/styledHelpers"
import { fontSize, fontWeight } from "buoy-theme"
import Error from "../../common/components/Error"
import Check from "../../completion/images/Check"
import AddInfoLink from "./AddInfoLink"
// 4851-todo: update responsive helper

const CardContainer = styled.section`
  ${CardStyle};
  position: relative;
  width: 100%;
  ${props =>
    props.zero
      ? `${responsive("padding", "32px 24px 24px", "24px 16px")};`
      : `${responsive("padding", "32px 24px", "24px 16px")};`};
  margin: ${props => props.margin || "0"};
  ${responsive("margin-bottom", "32px", "24px")};
  height: 100%;
`

const collapse = height => keyframes`
  0% {
    max-height: ${height}px;
    opacity: 1;
  }

  75% {
    opacity: 0;
  }

  100% {
    max-height: 0px;
    opacity: 0;
  }
`

const Collapsable = styled.div`
  ${props =>
    !props.expanded
      ? css`
          animation: ${collapse(props.height)} 0.5s forwards ease;
        `
      : ""};
`

const FixedArrow = styled.button`
  appearance: none;
  position: absolute;
  ${responsive("right", "24px", "16px")};
  ${responsive("top", "38px", "30px")};
  padding: 0;
  border: none;
  background: transparent;
  transition: all 0.5s ease;
  ${({ expanded }) =>
    expanded
      ? css`
          transform: rotate(-180deg);
        `
      : ""};

  :disabled {
    opacity: 0.5;
  }
`

const InnerContent = styled.div`
  ${responsive("padding-top", "40px", "32px")};
`

const CardHeading = styled(HeadingOne)`
  ${fontSize("bodyLarge")};
  font-weight: ${fontWeight.regular};
  text-align: left;
  line-height: 1.15;
  ${props =>
    props.inline
      ? css`
          margin: 0;
        `
      : `${responsive("margin", "0", "0 0 12px")};`};
`

const HeadingRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${responsive("align-items", "center", "flex-start")};
  ${props =>
    props.stack
      ? `${responsive("flex-direction", "row", "column")};`
      : css`
          justify-content: space-between;
        `};
`

const EditLink = styled(Link)`
  margin: 0 28px 0 auto;

  ${props =>
    props.fixed
      ? css`
    position: absolute;
    ${responsive("top", "30px", "22px")};
    ${responsive("right", "56px", "46px")};
    margin-right: 0;
  `
      : ""};
`

const savedAnimation = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`

const SavedMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 30px;
  ${responsive("top", "30px", "44px")};
  ${responsive("right", "98px", "16px")};
  opacity: 0;
  animation: ${savedAnimation} 0.5s ease forwards,
    ${savedAnimation} 1s ease 2s reverse;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;

  /* Responsive helper would be nasty here
    plus we're still using old breakpoints
    on siteportal */
  @media (min-width: 641px) {
    position: absolute;
    top: 24px;
    right: 24px;
    opacity: 1;
  }

  @media (max-width: 640px) {
    margin-top: 12px;
    position: relative;
  }
`

const PercentComplete = styled(Paragraph).attrs({
  color: "neutral",
})`
  ${responsive("margin", "0 0 0 24px", "0")};
  line-height: 1.25;
`

const ToggleSection = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  ${props =>
    props.expanded
      ? `
          ${responsive(
            "max-height",
            `${props.zero ? "108px" : "84px"}`,
            "100px"
          )};
        `
      : css`
          max-height: 100%;
        `};
`

const ProfileCard = ({
  name,
  edit,
  save,
  saved,
  cancel,
  error,
  expanded,
  collapsibleRef,
  collapsible,
  height,
  dirty,
  toggleExpanded,
  toggleMouseDown,
  percentComplete,
  showCompletion,
  disabled,
  expandContainer,
  onCardClick,
  ...props
}) => (
  <CardContainer margin="0 0 42px 0" zero={percentComplete === 0 && !dirty}>
    {!dirty && (
      <ToggleSection
        onClick={onCardClick}
        zero={percentComplete === 0}
        expanded={expanded}
      />
    )}
    <HeadingRow stack={percentComplete !== 0}>
      <CardHeading inline={percentComplete === 0}>{name}</CardHeading>
      {!dirty &&
        showCompletion && (
          <PercentComplete>{`${percentComplete}% complete`}</PercentComplete>
        )}
      {!dirty &&
        percentComplete !== 0 && (
          <EditLink fixed onClick={edit}>
            Edit
          </EditLink>
        )}
      {saved && (
        <SavedMessage>
          <Check color="neutral" />
          <Paragraph margin="0" color="neutral">
            Edits saved
          </Paragraph>
        </SavedMessage>
      )}
      {collapsible &&
        !dirty &&
        percentComplete !== 0 && (
          <FixedArrow
            disabled={dirty}
            onMouseDown={toggleMouseDown}
            onClick={toggleExpanded}
            expanded={expanded}
          >
            <Arrow />
          </FixedArrow>
        )}
    </HeadingRow>
    {!expanded &&
      percentComplete === 0 && (
        <AddInfoLink onClick={edit} underline={false}>
          {`+ Add ${name
            .toLowerCase()
            .replace(" information", "")} information`}
        </AddInfoLink>
      )}
    <Collapsable expanded={expanded} innerRef={collapsibleRef} height={height}>
      {expanded && (
        <InnerContent>
          {props.children}
          {error && <Error>{error}</Error>}
          <ButtonContainer collapsible={collapsible}>
            {dirty && (
              <React.Fragment>
                <EditLink onClick={cancel}>Cancel</EditLink>
                <Button onClick={save} disabled={disabled}>
                  Save edits
                </Button>
              </React.Fragment>
            )}
          </ButtonContainer>
        </InnerContent>
      )}
    </Collapsable>
  </CardContainer>
)

class ProfileCardWrapper extends React.Component {
  state = {
    height: 0,
    expanded: false,
  }

  componentDidMount() {
    this.setState({
      height: this.collapsible.getBoundingClientRect().height,
      expanded: this.props.dirty || this.props.defaultExpanded || false,
    })
  }

  componentDidUpdate = () => {
    if (
      this.props.percentComplete === 0 &&
      !this.props.dirty &&
      this.state.expanded &&
      !this.props.defaultExpanded
    ) {
      this.setState({ expanded: false })
    }
  }

  toggleExpanded = e => {
    this.setState({ expanded: !this.state.expanded })
  }

  toggleMouseDown = e => {
    e.preventDefault()
  }

  expandContainer = e => {
    if (!this.state.expanded) this.setState({ expanded: true })
  }

  edit = e => {
    if (e) e.stopPropagation()
    this.setState({ expanded: true, saved: false })
    this.props.edit()
  }

  save = () => {
    this.props
      .save()
      .then(() => {
        this.setState({ saved: true })
        setTimeout(() => this.setState({ saved: false }), 3000)
      })
      .catch(() => {
        this.setState({ saved: false })
      })
  }

  cancel = e => {
    if (e) e.stopPropagation()
    this.props.cancel()
  }

  onCardClick = e => {
    if (e) e.stopPropagation()
    const { percentComplete, dirty } = this.props
    const { expanded } = this.state
    if (dirty) return
    if (expanded || percentComplete !== 0) {
      this.toggleExpanded()
    } else {
      this.edit()
    }
  }

  render() {
    return (
      <ProfileCard
        {...this.props}
        collapsibleRef={c => (this.collapsible = c)}
        toggleExpanded={this.toggleExpanded}
        toggleMouseDown={this.toggleMouseDown}
        edit={this.edit}
        save={this.save}
        expandContainer={this.expandContainer}
        cancel={this.cancel}
        onCardClick={this.onCardClick}
        {...this.state}
      />
    )
  }
}

ProfileCardWrapper.defaultProps = {
  collapsible: true,
}

export {
  ProfileCardWrapper as default,
  CardHeading,
  CardContainer,
  ButtonContainer,
}



// WEBPACK FOOTER //
// ./assets/account/components/CollapsibleFormCard.js